<script setup lang="ts">
//#region import

import { onMounted, ref } from 'vue';

// Component
import PreviewFile from '@/shared/component/preview-file.vue';

// Model
import type { VistoriaEtapas } from '@/model/vistoria-etapas.interface';

// Other
import { TIPO_ETAPA_ASSINATURA } from '@/global';
import IconCamera from '@/shared/icons/IconCamera.vue';

//#endregion

const listMessageAction = {
  FOTO: 'Você precisa tirar uma foto',
  VIDEO: 'Você precisa gravar um vídeo',
  ASSINATURA: 'Você precisa assinar',
};

const listMessageCorrectionAction = {
  FOTO: 'É necessário corrigir essa foto',
  VIDEO: 'É necessário corrigir esse vídeo',
  ASSINATURA: '',
};

const listTitle = {
  FOTO: 'Tirar Foto',
  VIDEO: 'Gravar Vídeo',
  ASSINATURA: 'Assinar',
};

const listTitleCorrection = {
  FOTO: 'Corrigir Foto',
  VIDEO: 'Corrigir Vídeo',
  ASSINATURA: 'Assinar',
};

const messageAction = ref<string>();
const needActionRequired = ref<boolean>();
const isCorrection = ref<boolean>(false);
const titleAction = ref<string>();
const needAction = ref<boolean>();

const emit = defineEmits(['retry']);

const props = defineProps<{
  item: VistoriaEtapas;
}>();

const retry = (idEtapa: string) => {
  emit('retry', idEtapa);
};

onMounted(() => {
  const tipo = props.item.tipo as 'FOTO' | 'VIDEO' | 'ASSINATURA';

  isCorrection.value = props.item?.fotoVideo?.aprovada === false;
  messageAction.value = isCorrection.value
    ? listMessageCorrectionAction[tipo]
    : listMessageAction[tipo];
  needActionRequired.value =
    (!props.item.resumo?.fileContent || props.item?.redo) &&
    props.item.obrigatorio;
  titleAction.value = isCorrection.value
    ? listTitleCorrection[tipo]
    : listTitle[tipo];

  needAction.value = !props.item.resumo?.fileContent || props.item?.redo;
});
</script>

<template>
  <div class="item-resumo-file">
    <span class="texto item-resumo-file__action" v-if="needActionRequired">
      {{ messageAction }}
    </span>

    <div
      class="card item-resumo-file__container"
      :class="{ reprovada: isCorrection }"
    >
      <PreviewFile
        :file-content="props.item.resumo?.fileContent"
        :type-mine="props.item.resumo?.type"
        :tipo-arquivo="props.item.tipo"
        @retry="retry(props.item.idEtapa)"
        :placeholder-video="true"
      >
        <div class="item-resumo-file__container__items">
          <!-- Observações -->
          <span class="texto" v-if="props.item?.fotoVideo?.observacaoVistoria"
            >Observação: {{ props.item?.fotoVideo.observacaoVistoria }}</span
          >

          <!-- Etapa Reprovada -->
          <div
            class="reprovada__container"
            v-if="isCorrection && props.item.tipo !== TIPO_ETAPA_ASSINATURA"
          >
            <div>
              <span
                class="texto-reprovada"
                v-if="props.item.fotoVideo?.motivoReprovacao"
                >{{ props.item.fotoVideo?.motivoReprovacao }}</span
              >
            </div>
          </div>

          <!-- Botão de Ação -->
          <div
            class="btn-vistoria"
            v-if="props.item.resumo?.fileContent && !props.item?.redo"
          >
            <button
              class="btn btn-link"
              @click="retry(props.item.idEtapa)"
              data-testId="redo-button"
            >
              Refazer
            </button>
          </div>
        </div>
      </PreviewFile>
    </div>
    <button
      class="card button-take-gallery-file"
      :class="{ correction: isCorrection }"
      v-if="needAction"
      @click="retry(props.item.idEtapa)"
      data-testId="action-button"
    >
      <IconCamera />
      {{ titleAction }}
    </button>
  </div>
</template>

<style lang="scss">
.item-resumo-file {
  margin-bottom: 1.6rem;

  &__action {
    color: var(--alert);
  }

  &__container {
    &.reprovada {
      img {
        opacity: 0.5;
      }

      .reprovada__container {
        text-align: center;
        padding: 0 0.2rem;

        .texto-reprovada {
          font-size: 0.7rem;
          color: var(--danger);
        }
      }
    }

    .preview-pdf,
    .preview-image {
      margin-top: 0;
      border: none;
    }

    &__items {
      width: 100%;

      span.texto {
        padding: 0.4rem 0;
        display: block;
        width: 100%;
        text-align: left;
      }

      .btn-vistoria {
        margin-top: 0.4rem;
      }
    }
  }

  .button-take-gallery-file {
    cursor: pointer;
    margin-top: 0.8rem;
    padding: 0.8rem;
    display: flex;
    flex-direction: row !important;
    justify-content: center;
    align-items: center;
    font-weight: 500;
    color: var(--primary);
    width: 100%;
    text-transform: uppercase;
    position: relative;

    svg {
      position: absolute;
      display: block;
      width: 1.4rem;
      height: 1.4rem;
      left: 0.8rem;
    }

    &.correction {
      color: var(--alert);
    }
  }
}
</style>
